import React, { useState, useEffect } from 'react';
import { Clock, Gift, TrendingUp } from 'lucide-react';

const EarlyPaymentBanner = ({ 
  invoice, 
  viewType = 'receiver', // 'receiver' or 'sender'
  compact = false // For mobile/list view
}) => {
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isExpired, setIsExpired] = useState(false);

  // Get early payment discount data
  const getEarlyPaymentData = () => {
    // Debug logging
    console.log('🎁 EarlyPaymentBanner - Invoice data:', {
      hasInvoice: !!invoice,
      invoiceId: invoice?.id || invoice?.invoiceNumber,
      invoiceNumber: invoice?.invoiceNumber,
      status: invoice?.status,
      discountAmount: invoice?.discountAmount,
      hasDiscountAmount: !!invoice?.discountAmount,
      discountSourceInvoice: invoice?.discountSourceInvoice,
      hasDiscounts: !!invoice?.discounts,
      discountsLength: invoice?.discounts?.length || 0,
      discountsData: invoice?.discounts
    });

    if (!invoice || !invoice.discounts || invoice.discounts.length === 0) {
      return null;
    }

    // ✅ SHOW CANCELLED BANNER FOR REJECTED INVOICES WITH DISCOUNTS
    // This informs users that the early payment discount was cancelled
    if (invoice.status === 'rejected' || invoice.status === 'cancelled') {
      const discount = invoice.discounts[0];
      if (discount) {
        return { ...discount, status: 'cancelled' };
      }
      return null;
    }

    const discount = invoice.discounts[0]; // Get first (should only be one)
    
    // ✅ CORRECT LOGIC: Check if discount was EARNED (invoice paid within window)
    // NOT if it's been used/applied to another invoice yet
    
    // If invoice is PAID, check if discount was earned
    if (invoice.status === 'paid' && invoice.paymentDate) {
      const paymentDate = new Date(invoice.paymentDate);
      const expiryDate = new Date(discount.expiresAt);
      
      // Paid within window = EARNED (regardless of isUsed)
      if (paymentDate <= expiryDate) {
        return { ...discount, status: 'earned' };
      } else {
        // Paid after window = EXPIRED
        return { ...discount, status: 'expired' };
      }
    }

    // For unpaid invoices, check if offer is still active
    const expiryDate = new Date(discount.expiresAt);
    const now = new Date();
    
    if (now > expiryDate) {
      return { ...discount, status: 'expired' };
    }

    return { ...discount, status: 'active' };
  };

  const earlyPayment = getEarlyPaymentData();

  // Calculate time remaining
  useEffect(() => {
    if (!earlyPayment || earlyPayment.status !== 'active') {
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const expiry = new Date(earlyPayment.expiresAt);
      const diffMs = expiry - now;

      if (diffMs <= 0) {
        setIsExpired(true);
        setTimeRemaining(null);
        return;
      }

      const diffSeconds = Math.floor(diffMs / 1000);
      const diffMinutes = Math.floor(diffSeconds / 60);
      const diffHours = Math.floor(diffMinutes / 60);
      const diffDays = Math.floor(diffHours / 24);

      const hours = diffHours % 24;
      const minutes = diffMinutes % 60;

      // Format based on time remaining
      if (diffHours > 48) {
        setTimeRemaining(`${diffDays} day${diffDays !== 1 ? 's' : ''}`);
      } else if (diffHours >= 24) {
        setTimeRemaining(`${diffDays} day${diffDays !== 1 ? 's' : ''} ${hours}h`);
      } else if (diffHours >= 6) {
        setTimeRemaining(`${diffHours}h`);
      } else if (diffHours >= 1) {
        setTimeRemaining(`${diffHours}h ${minutes}m`);
      } else {
        setTimeRemaining(`${diffMinutes}m`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [earlyPayment]);

  // Calculate progress percentage
  const getProgressPercentage = () => {
    if (!earlyPayment || earlyPayment.status !== 'active') return 0;

    const createdAt = new Date(earlyPayment.createdAt);
    const expiresAt = new Date(earlyPayment.expiresAt);
    const now = new Date();

    const totalDuration = expiresAt - createdAt;
    const elapsed = now - createdAt;
    const remaining = (totalDuration - elapsed) / totalDuration;

    return Math.max(0, Math.min(100, remaining * 100));
  };

  // Get color based on time remaining
  const getColorScheme = () => {
    const percentage = getProgressPercentage();
    
    if (percentage > 50) {
      return {
        bg: 'bg-green-50',
        border: 'border-green-200',
        text: 'text-green-700',
        icon: 'text-green-600',
        progress: 'bg-green-500',
        button: 'bg-green-600 hover:bg-green-700'
      };
    } else if (percentage > 25) {
      return {
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        text: 'text-yellow-700',
        icon: 'text-yellow-600',
        progress: 'bg-yellow-500',
        button: 'bg-yellow-600 hover:bg-yellow-700'
      };
    } else {
      return {
        bg: 'bg-red-50',
        border: 'border-red-200',
        text: 'text-red-700',
        icon: 'text-red-600',
        progress: 'bg-red-500',
        button: 'bg-red-600 hover:bg-red-700'
      };
    }
  };

  // Don't render if no early payment discount
  if (!earlyPayment) {
    return null;
  }

  // Show different states to receiver (removed the hiding logic)
  // Receivers now see claimed/expired states for feedback

  const colors = getColorScheme();
  const progressPercentage = getProgressPercentage();
  const discountText = earlyPayment.discountAmount 
    ? `£${parseFloat(earlyPayment.discountAmount).toFixed(2)}`
    : `${earlyPayment.discountPercentage}%`;

  // Compact view for list/mobile
  if (compact) {
    if (viewType === 'sender') {
      return (
        <div className="flex items-center space-x-2 text-xs">
          <Gift className="w-4 h-4 text-blue-600" />
          <span className="text-blue-700 font-medium">
            Early Payment ({earlyPayment.timeframeDays}d)
          </span>
          {earlyPayment.status === 'active' && (
            <span className={`px-2 py-0.5 rounded-full ${colors.bg} ${colors.text} font-medium`}>
              Active
            </span>
          )}
          {earlyPayment.status === 'expired' && (
            <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">
              Expired
            </span>
          )}
          {earlyPayment.status === 'earned' && (
            <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium">
              Earned
            </span>
          )}
          {earlyPayment.status === 'cancelled' && (
            <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
              Cancelled
            </span>
          )}
        </div>
      );
    }

    // Compact receiver view - Earned state
    if (earlyPayment.status === 'earned') {
      return (
        <div className="flex items-center justify-between p-3 rounded-lg border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-blue-50">
          <div className="flex items-center space-x-2">
            <Gift className="w-5 h-5 text-purple-600" />
            <div>
              <div className="text-sm font-bold text-purple-900">
                ✅ EARNED: {discountText} off next invoice
              </div>
              <div className="text-xs text-purple-600">
                Will apply automatically
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Compact receiver view - Expired state
    if (earlyPayment.status === 'expired') {
      return (
        <div className="flex items-center justify-between p-3 rounded-lg border-2 border-gray-300 bg-gray-50">
          <div className="flex items-center space-x-2">
            <Clock className="w-5 h-5 text-gray-600" />
            <div>
              <div className="text-sm font-bold text-gray-700">
                ⏰ Bonus expired
              </div>
              <div className="text-xs text-gray-600">
                Paid after window
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Compact receiver view - Cancelled state
    if (earlyPayment.status === 'cancelled') {
      return (
        <div className="flex items-center justify-between p-3 rounded-lg border-2 border-red-300 bg-red-50">
          <div className="flex items-center space-x-2">
            <span className="text-xl">🚫</span>
            <div>
              <div className="text-sm font-bold text-red-900">
                Discount Cancelled
              </div>
              <div className="text-xs text-red-700">
                Invoice rejected
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Compact receiver view - Active state
    return (
      <div className={`flex items-center justify-between p-3 rounded-lg border-2 ${colors.border} ${colors.bg}`}>
        <div className="flex items-center space-x-2">
          <Gift className={`w-5 h-5 ${colors.icon}`} />
          <div>
            <div className={`text-sm font-bold ${colors.text}`}>
              BONUS: {discountText} off next invoice
            </div>
            <div className={`text-xs ${colors.text}`}>
              Pay within {timeRemaining}
            </div>
          </div>
        </div>
        <div className="w-20">
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className={`h-full ${colors.progress} transition-all duration-500`}
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          <div className={`text-xs text-center mt-1 ${colors.text} font-medium`}>
            {Math.round(progressPercentage)}%
          </div>
        </div>
      </div>
    );
  }

  // RECEIVER VIEW: Show earned state with success message
  if (viewType === 'receiver' && earlyPayment.status === 'earned') {
    return (
      <div className="rounded-xl border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-blue-50 p-6 mb-6 shadow-sm">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-3 rounded-full bg-purple-100 border-2 border-purple-300">
              <Gift className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-purple-900">
                🎉 BONUS EARNED: {discountText} OFF YOUR NEXT INVOICE!
              </h3>
              <p className="text-sm text-purple-700 mt-1">
                You paid early! This discount will automatically apply to your next invoice from {invoice.sender?.companyName || 'this sender'}.
              </p>
              <p className="text-xs text-purple-600 mt-2 font-medium">
                Status: Ready to use • Will apply automatically
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // RECEIVER VIEW: Show expired state with information
  if (viewType === 'receiver' && earlyPayment.status === 'expired') {
    return (
      <div className="rounded-xl border-2 border-gray-300 bg-gray-50 p-6 mb-6 shadow-sm">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-3 rounded-full bg-gray-200 border-2 border-gray-300">
              <Clock className="w-6 h-6 text-gray-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-700">
                ⏰ Early Payment Bonus Expired
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                This invoice was paid after the early payment window. The {discountText} discount is no longer available.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // RECEIVER VIEW: Show cancelled state for rejected/unapproved invoices
  if (viewType === 'receiver' && earlyPayment.status === 'cancelled') {
    return (
      <div className="rounded-xl border-2 border-red-300 bg-red-50 p-6 mb-6 shadow-sm">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-3 rounded-full bg-red-100 border-2 border-red-300">
              <span className="text-2xl">🚫</span>
            </div>
            <div>
              <h3 className="text-lg font-bold text-red-900">
                Early Payment Discount Cancelled
              </h3>
              <p className="text-sm text-red-700 mt-1">
                The {discountText} early payment opportunity was cancelled when this invoice was rejected.
              </p>
              <p className="text-xs text-red-600 mt-2">
                No payment will be processed and no discount can be earned from this invoice.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // SENDER VIEW: Show cancelled state for rejected/unapproved invoices
  if (viewType === 'sender' && earlyPayment.status === 'cancelled') {
    return (
      <div className="rounded-xl border-2 border-red-300 bg-red-50 p-6 mb-6 shadow-sm">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-3 rounded-full bg-red-100 border-2 border-red-300">
              <span className="text-2xl">🚫</span>
            </div>
            <div>
              <h3 className="text-lg font-bold text-red-900">
                Early Payment Discount Cancelled
              </h3>
              <p className="text-sm text-red-700 mt-1">
                The {discountText} early payment incentive was cancelled when this invoice was rejected/unapproved.
              </p>
              <p className="text-xs text-red-600 mt-2">
                Customer will not receive this discount. Please submit a new invoice if needed.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Full banner view for active discount or sender view
  return (
    <div className={`rounded-xl border-2 ${colors.border} ${colors.bg} p-6 mb-6 shadow-sm`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className={`p-3 rounded-full ${colors.bg} border-2 ${colors.border}`}>
            <Gift className={`w-6 h-6 ${colors.icon}`} />
          </div>
          <div>
            <h3 className={`text-lg font-bold ${colors.text}`}>
              {viewType === 'receiver' ? '🎁 EARLY PAYMENT BONUS AVAILABLE!' : '⚡ Early Payment Incentive Active'}
            </h3>
            <p className={`text-sm ${colors.text} mt-1`}>
              {viewType === 'receiver' 
                ? `Pay within ${timeRemaining} to receive ${discountText} discount on your next invoice from ${invoice.sender?.companyName || 'this sender'}`
                : `Receiver gets ${discountText} off their next invoice if paid within ${earlyPayment.timeframeDays} days`
              }
            </p>
          </div>
        </div>
        
        {earlyPayment.status === 'active' && (
          <div className="flex items-center space-x-2">
            <Clock className={`w-5 h-5 ${colors.icon}`} />
            <div className="text-right">
              <div className={`text-2xl font-bold ${colors.text}`}>
                {timeRemaining}
              </div>
              <div className={`text-xs ${colors.text}`}>
                remaining
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Progress bar */}
      {earlyPayment.status === 'active' && (
        <div className="mb-4">
          <div className="flex justify-between text-xs mb-2">
            <span className={colors.text}>Time Remaining</span>
            <span className={`font-bold ${colors.text}`}>{Math.round(progressPercentage)}%</span>
          </div>
          <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className={`h-full ${colors.progress} transition-all duration-500 flex items-center justify-end pr-2`}
              style={{ width: `${progressPercentage}%` }}
            >
              {progressPercentage > 20 && (
                <span className="text-white text-xs font-bold">{Math.round(progressPercentage)}%</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Benefits section for receiver */}
      {viewType === 'receiver' && earlyPayment.status === 'active' && (
        <div className={`flex items-center space-x-4 p-4 bg-white rounded-lg border ${colors.border}`}>
          <TrendingUp className={`w-6 h-6 ${colors.icon}`} />
          <div className="flex-1">
            <div className="font-semibold text-gray-900">Your Benefit</div>
            <div className="text-sm text-gray-600">
              Save {discountText} on your next invoice by paying now
            </div>
          </div>
          <div className={`text-2xl font-bold ${colors.text}`}>
            {discountText}
          </div>
        </div>
      )}

      {/* Status badges */}
      {earlyPayment.status === 'expired' && viewType === 'sender' && (
        <div className="p-4 bg-gray-100 rounded-lg border border-gray-300">
          <div className="flex items-center space-x-2">
            <Clock className="w-5 h-5 text-gray-600" />
            <span className="font-semibold text-gray-700">Early Payment Window Expired</span>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            This invoice was not paid within the {earlyPayment.timeframeDays}-day early payment window.
          </p>
        </div>
      )}

      {earlyPayment.status === 'earned' && viewType === 'sender' && (
        <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
          <div className="flex items-center space-x-2">
            <Gift className="w-5 h-5 text-purple-600" />
            <span className="font-semibold text-purple-700">Discount Earned!</span>
          </div>
          <p className="text-sm text-purple-600 mt-1">
            Receiver paid on time and earned the {discountText} discount.
          </p>
        </div>
      )}
    </div>
  );
};

export default EarlyPaymentBanner;

